import { z } from "zod";

/**
 * Rate Limit Settings Schema
 *
 * SHARED across all deployment types that expose public endpoints.
 * Currently used by: EMBED, OUTLOOK_ADDIN, EXCEL, WORD, POWERPOINT
 *
 * Why shared?
 * - Rate limiting is a cross-cutting security concern
 * - Same validation rules apply to all deployment types
 * - DRY: Single source of truth for rate limit configuration
 *
 * @example
 * ```typescript
 * const settings = RateLimitSettingsSchema.parse({
 *   rateLimiting: true,
 *   rateLimitingLimit: 100,
 *   rateLimitingInterval: 60
 * });
 * ```
 */
export const RateLimitSettingsSchema = z.object({
  /** Whether rate limiting is enabled for this deployment */
  rateLimiting: z.boolean().default(true),

  /** Maximum number of requests allowed in the interval */
  rateLimitingLimit: z.number().int().positive().default(50),

  /** Time window in seconds for the rate limit */
  rateLimitingInterval: z.number().int().positive().default(60)
});

/**
 * Inferred TypeScript type from RateLimitSettingsSchema
 */
export type RateLimitSettings = z.infer<typeof RateLimitSettingsSchema>;

/**
 * Default rate limit settings
 *
 * Used when:
 * - Creating new deployments
 * - Migration for existing deployments without rate limit settings
 * - Fallback when settings are invalid
 */
export const DEFAULT_RATE_LIMIT_SETTINGS: RateLimitSettings = {
  rateLimiting: true,
  rateLimitingLimit: 50,
  rateLimitingInterval: 60
};
